/**
 * Settings.ts — Game settings with localStorage persistence for Gallowcrest.
 *
 * Stores player preferences for text speed, accessibility,
 * and audio (audio non-functional until Phase 5).
 */

// ─── Settings Interface ──────────────────────────────────────

export interface GameSettings {
  textSpeed: 'instant' | 'fast' | 'normal' | 'slow';
  colorblindMode: boolean;
  highContrast: boolean;
  statCheckAnimation: boolean;
  fontSize: number;      // 12, 14, 16, 18, 20
  crtEffect: boolean;    // CRT scanlines + phosphor glow
  particles: boolean;    // Ambient particle effects
  particlePreset: string; // Ambient particle preset (snow, embers, arcane, mist)
  terminalFrame: boolean; // Frost Citadel CSS frame overlay
  autoAdvanceContinue: boolean; // Auto-advance single-choice "continue" screens
  musicVolume: number;   // 0-100 (stored but not used until Phase 5)
  sfxVolume: number;     // 0-100 (stored but not used until Phase 5)
}

// ─── Defaults ────────────────────────────────────────────────

const DEFAULT_SETTINGS: GameSettings = {
  textSpeed: 'normal',
  colorblindMode: false,
  highContrast: false,
  statCheckAnimation: true,
  fontSize: 16,
  crtEffect: true,
  particles: true,
  particlePreset: 'snow',
  terminalFrame: true,
  autoAdvanceContinue: true,
  musicVolume: 80,
  sfxVolume: 80,
};

const STORAGE_KEY = 'gallowcrest_settings';

// ─── Typewriter Speed Map ────────────────────────────────────

const TYPEWRITER_SPEEDS: Record<GameSettings['textSpeed'], number> = {
  instant: 0,
  fast: 8,
  normal: 20,
  slow: 40,
};

// ─── SettingsManager ─────────────────────────────────────────

export class SettingsManager {
  private settings: GameSettings;

  constructor() {
    this.settings = { ...DEFAULT_SETTINGS };
    this.load();
  }

  /** Get current settings (read-only copy). */
  get(): Readonly<GameSettings> {
    return this.settings;
  }

  /** Update one or more settings and persist. */
  update(partial: Partial<GameSettings>): void {
    this.settings = { ...this.settings, ...partial };
    this.save();
  }

  /** Get the typewriter speed in ms per character. */
  getTypewriterSpeed(): number {
    return TYPEWRITER_SPEEDS[this.settings.textSpeed];
  }

  /** Check if typewriter is effectively disabled. */
  isTypewriterDisabled(): boolean {
    return this.settings.textSpeed === 'instant';
  }

  /** Load settings from localStorage. */
  load(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        this.settings = { ...DEFAULT_SETTINGS, ...parsed };
      }
    } catch {
      // Use defaults on parse error
    }
  }

  /** Save settings to localStorage. */
  save(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings));
    } catch {
      // Storage unavailable
    }
  }
}
